import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { fitOf, type Fit } from './jewellery';

/**
 * Hair made in code, strand by strand, for the styles the MakeHuman set doesn't have: box braids (long, or a bob),
 * a curly wig, long bone-straight… Each strand starts at a root on the scalp, runs back over the head (nothing falls
 * over the face) and then hangs, pushed out over whatever's under it (the neck, the shoulders, a collar) instead of
 * through it. One draw per head.
 * Appearance.hair = 'code:<id>'.
 */

export interface HairdoDef {
  id: string; label: string;
  /** strands, each strand's thickness (m), how far below the head's middle it ends (m) */
  count: number; thick: number; length: number;
  /** a curl: the helix's radius and turns per metre (none: straight) */
  curl?: [number, number];
  /** how far out from the scalp the hair stands (volume) */
  lift: number;
  /** the surface: plaited (braids) or smooth */
  plait: boolean;
  /** some strands fall in front of the shoulders */
  front: number;
  /** painted under it */
  scalp: 'buzz' | 'none' | 'under';
  /** parted (a side part at this x, -1..1 across the head): each strand lies away from the part, over the side of
   * the head, and falls by the face and down past the shoulders; else it's swept straight back */
  part?: number;
  /** roots start this far inside the hairline (braids: a little in; a wig's lace front: right at it) */
  hairline?: number;
  for?: 'f' | 'm';
}

export const HAIRDOS: HairdoDef[] = [
  // (braids start right at the hairline, the scalp under them dark: no painted rows showing at the front)
  { id: 'code:boxbraids', label: 'Box braids', count: 460, thick: 0.0042, length: 0.42, lift: 0.008, plait: true, front: 0.3, scalp: 'under', hairline: -0.2, for: 'f' , part: 0.18 },
  { id: 'code:knotless-bob', label: 'Braided bob', count: 420, thick: 0.004, length: 0.16, lift: 0.008, plait: true, front: 0.15, scalp: 'under', hairline: -0.2, for: 'f' , part: 0.18 },
  { id: 'code:curly', label: 'Curly wig', count: 470, thick: 0.0036, length: 0.2, curl: [0.0062, 75], lift: 0.034, plait: false, front: 0.35, scalp: 'under', hairline: -0.31, for: 'f' , part: 0.18 },
];
/** (twists and cornrows were dropped: a life saved wearing them gets box braids) */
export const hairdoById = (id: string | null | undefined) => (id ? HAIRDOS.find((h) => h.id === (id === 'code:twists' || id === 'code:cornrows' ? 'code:boxbraids' : id)) : undefined);
export const isCodeHair = (id: string | null | undefined) => !!id && id.startsWith('code:');

let plaitTex: THREE.Texture | null = null;
/** a strand's surface: diagonal bands one way then the other (the over-under of a plait), lighter on the ridges */
function plaitTexture(): THREE.Texture {
  if (plaitTex) return plaitTex;
  // u along the strand, v round it: a herringbone (each half of the way round leans the other way), dark in the gaps
  const W = 64, Hh = 32, c = document.createElement('canvas'); c.width = W; c.height = Hh;
  const g = c.getContext('2d')!, img = g.createImageData(W, Hh);
  for (let y = 0; y < Hh; y++) for (let x = 0; x < W; x++) {
    const v = y / Hh, dir = v < 0.5 ? 1 : -1, t = x / W * 2 + (v % 0.5) * 2 * dir;
    const ridge = Math.pow(Math.abs(Math.sin(t * Math.PI)), 0.6);
    const k = Math.round(70 + 185 * ridge * (0.85 + 0.15 * Math.sin(v * Math.PI * 4)));
    const o = (y * W + x) * 4; img.data[o] = img.data[o + 1] = img.data[o + 2] = k; img.data[o + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  plaitTex = new THREE.CanvasTexture(c);
  plaitTex.wrapS = plaitTex.wrapT = THREE.RepeatWrapping;
  plaitTex.colorSpace = THREE.SRGBColorSpace;
  return plaitTex;
}

/** the head as a ball (centre, radius) and the scalp's actual surface reach in any direction */
function headOf(f: Fit) {
  const hi = f.bones.findIndex((b) => b.name === 'head');
  const s = f.body, pts: THREE.Vector3[] = [];
  const eyeY = f.eye ? (f.eye.min.y + f.eye.max.y) / 2 : -Infinity;
  // (the head bone's skin finds the head; then all the skin round it above the eyes, whichever bone moves it: the
  // forehead belongs to the face's own bones, and a scalp measured without it puts the hair inside the brow)
  const own: THREE.Vector3[] = [];
  for (let i = 0; i < s.bone.length; i++) if (s.bone[i] === hi && s.pos[i * 3 + 1] > eyeY - 0.03) own.push(new THREE.Vector3(s.pos[i * 3], s.pos[i * 3 + 1], s.pos[i * 3 + 2]));
  const C0 = new THREE.Box3().setFromPoints(own).getCenter(new THREE.Vector3());
  for (let i = 0; i < s.bone.length; i++) { const p = new THREE.Vector3(s.pos[i * 3], s.pos[i * 3 + 1], s.pos[i * 3 + 2]); if (p.y > eyeY - 0.03 && p.distanceTo(C0) < 0.16) pts.push(p); }
  const box = new THREE.Box3().setFromPoints(pts), H = box.getCenter(new THREE.Vector3());
  H.y = Math.min(H.y, eyeY + 0.035);
  const R = pts.reduce((m, p) => Math.max(m, p.distanceTo(H)), 0);
  const reach = (d: THREE.Vector3) => { let r = 0; for (const p of pts) { const v = p.clone().sub(H), l = v.length(); if (l > 0 && v.dot(d) / l > 0.975) r = Math.max(r, v.dot(d)); } return r || R * 0.9; };
  return { H, R, reach, eyeY };
}

/** the torso's outer reach round a vertical axis, by height and angle: hanging hair is pushed outside it */
function bodyMap(f: Fit, cx: number, cz: number, top: number) {
  const dy = 0.01, nA = 48, nY = 80, map = new Float32Array(nY * nA);
  const s = f.dressed;
  for (let i = 0; i < s.bone.length; i++) {
    const x = s.pos[i * 3] - cx, y = s.pos[i * 3 + 1], z = s.pos[i * 3 + 2] - cz;
    if (Math.abs(x) > 0.22) continue; // (the arms hang outside this)
    const yi = Math.floor((top - y) / dy);
    if (yi < 0 || yi >= nY) continue;
    const a = Math.floor(((Math.atan2(x, z) + Math.PI) / (Math.PI * 2)) * nA) % nA;
    map[yi * nA + a] = Math.max(map[yi * nA + a], Math.hypot(x, z));
  }
  return (p: THREE.Vector3, pad: number) => {
    const yi = Math.floor((top - p.y) / dy);
    if (yi < 0 || yi >= nY) return;
    const x = p.x - cx, z = p.z - cz, a = Math.floor(((Math.atan2(x, z) + Math.PI) / (Math.PI * 2)) * nA) % nA;
    let r = 0;
    for (let k = -1; k <= 1; k++) r = Math.max(r, map[yi * nA + ((a + k + nA) % nA)]);
    const cur = Math.hypot(x, z);
    if (r > 0 && cur < r + pad) { const k = (r + pad) / (cur || 1); p.x = cx + x * k; p.z = cz + z * k; }
  };
}

/**
 * The hair's roots: spread over the scalp inside the hairline, clear of the ears, denser toward the front. Every
 * front strand lies back over the crown, so the front needs the most (spread evenly, the big back of the head took
 * most of them and the hairline looked bald).
 */
function roots(n: number, margin = 0.04, even = false): THREE.Vector3[] {
  const cand: [THREE.Vector3, number][] = [], N = n * 10;
  for (let i = 0; i < N; i++) {
    const y = 1 - (2 * (i + 0.5)) / N, r = Math.sqrt(1 - y * y), t = i * Math.PI * (3 - Math.sqrt(5));
    const d = new THREE.Vector3(Math.cos(t) * r, y, Math.sin(t) * r);
    const line = d.z > 0 ? 0.2 + 0.45 * d.z : 0.16 + 0.55 * d.z; // (the hairline: high at the forehead, down to the nape)
    const ears = Math.abs(d.x) > 0.86 && d.y < 0.18 && d.z > -0.35; // (just the ear itself: the temples and above the ear get hair)
    // (the margin bites hardest at the front: measured from this head's centre the forehead hairline sits low)
    const m = margin * (d.z > 0 ? 0.4 + 1.6 * d.z : 0.4) + (margin < -0.1 && d.z > -0.35 ? -0.35 * Math.max(0, Math.abs(d.x) - 0.55) : 0); // (down to the sideburns in front of the ear)
    if (d.y > line + m && !ears) cand.push([d, even ? 1 + 1.2 * Math.max(0, d.z) + (d.z > 0 && d.y < line + m + 0.1 ? 2 : 0) + (d.y > 0.85 ? 1.5 : 0) : 1 + 3 * Math.max(0, d.z) + 0.8 * Math.max(0, d.y) - 0.5 * Math.max(0, -d.z - 0.3) + (d.z > 0 && d.y < line + m + 0.12 ? 3 : 0) + (Math.abs(d.x) > 0.55 && d.y < 0.6 && d.z > -0.4 ? 5 : 0)]); // (and the side edges, by the temples and over the ears) // (the edge itself thickest: a hairline, not a gap)
  }
  // (stratified by weight along the spiral, which keeps them spread out)
  const W = cand.reduce((a, [, w]) => a + w, 0), out: THREE.Vector3[] = [];
  let acc = 0, next = W / n / 2;
  for (const [d, w] of cand) { acc += w; while (acc >= next && out.length < n) { out.push(d); next += W / n; } }
  return out;
}

function build(def: HairdoDef, f: Fit, colour: string): THREE.Mesh | null {
  const head = headOf(f);
  if (!head.R) return null;
  const { H, reach } = head;

  const push = bodyMap(f, H.x, H.z - 0.02, H.y - head.R * 0.4);
  const geos: THREE.BufferGeometry[] = [];
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  for (const d of roots(def.count, def.hairline ?? 0.04, def.part !== undefined)) {
    const forward = Math.abs(d.x) > 0.45 && rnd() < def.front;
    const side = Math.sign(d.x) || 1;
    const pts: THREE.Vector3[] = [];
    const parted = def.part !== undefined;
    if (parted) {
      // away from the part, flat on the scalp, to the side of the head at temple height (out past the eyes: the front
      // ones end in front of the ear, the back ones behind it), each step a hair higher so the strands from near the
      // part lie over the ones rooted lower down
      const sd = Math.sign(d.x - def.part!) || 1;
      // (the back of the head falls down the back; the rest to the side, diagonally down from the part, not along the hairline)
      const back = THREE.MathUtils.smoothstep(-d.z, 0.05, 0.45);
      const T = new THREE.Vector3(sd * 0.97 * (1 - back) + d.x * 0.6 * back, -0.05 - 0.3 * back, d.z * 0.35 * (1 - back) - 0.95 * back).normalize();
      const K = Math.max(1, Math.ceil(d.angleTo(T) / 0.07));
      for (let k = 0; k <= K; k++) {
        const dir = d.clone().lerp(T, k / K).normalize();
        pts.push(H.clone().addScaledVector(dir, k === 0 ? reach(dir) - def.thick * 1.2 : reach(dir) + def.thick + Math.min(0.012, def.lift + 0.0004 * k)));
      }
    } else {
      // straight back, flat on the scalp from the root (at the hairline, for the front ones), along a line parallel to
      // its neighbours (a circle round the ear-to-ear axis), until it's past the crown: there it leaves the head and
      // falls. Each step back it sits a hair higher, so the strands from in front lie over the ones rooted behind.
      // (the ones that end up over a shoulder in front leave the head at the side, behind the ear: never across the face)
      const sx = THREE.MathUtils.clamp(d.x, -0.95, 0.95), q = Math.sqrt(1 - sx * sx), exitY = forward ? 0.15 : -0.05 - 0.3 * Math.abs(sx);
      let t = Math.atan2(d.y, d.z);
      if (t < 0) t += Math.PI * 2; // (low on the back of the head: onward from there, never the long way round under the chin)
      for (let k = 0; k < 60; k++, t += 0.07) {
        const dir = new THREE.Vector3(sx, q * Math.sin(t), q * Math.cos(t));
        pts.push(H.clone().addScaledVector(dir, k === 0 ? reach(dir) - def.thick * 1.2 : reach(dir) + def.thick + Math.min(0.012, def.lift + 0.00045 * k))); // (the root tucked into the scalp: it grows out of it, no cut end showing)
        if (dir.y < exitY && t > Math.PI * 0.5) break;
      }
    }
    // then it hangs, a little out and back, kept outside the neck and shoulders
    const last = pts[pts.length - 1].clone(), end = H.y - def.length - rnd() * 0.04;
    const drift = parted
      ? new THREE.Vector3((Math.sign(d.x - def.part!) || 1) * (0.015 + rnd() * 0.025), 0, d.z > 0.2 ? rnd() * 0.02 : -0.03 - rnd() * 0.04) // (straight down by the face; the back ones down the back)
      : new THREE.Vector3(side * (forward ? 0.1 : 0.04) * (0.6 + rnd() * 0.8), 0, forward ? 0.09 : -0.06 - rnd() * 0.04);
    const fall = Math.max(0.01, last.y - end), segs = Math.max(2, Math.ceil(fall / 0.03));
    for (let k = 1; k <= segs; k++) {
      // (out to the side at once, but forward only once it's below the chin: nothing swings across the face)
      const t = k / segs, dropped = fall * t, fwd = THREE.MathUtils.smoothstep(dropped, 0.14, 0.3);
      const p = last.clone().addScaledVector(drift, Math.sqrt(t) * fall / 0.3);
      if (!parted) p.z = last.z + (p.z - last.z) * (drift.z > 0 ? fwd : 1);
      p.y = last.y - dropped;
      push(p, def.thick + 0.004 + def.lift * 0.5);
      pts.push(p);
    }
    let path = pts;
    if (def.curl) { // a loose helix round the path
      const curve = new THREE.CatmullRomCurve3(pts), len = curve.getLength(), n = Math.max(12, Math.floor(len * 160));
      const [amp, turns] = def.curl, ph = rnd() * Math.PI * 2;
      const frames = curve.computeFrenetFrames(n, false);
      path = curve.getSpacedPoints(n).map((p, i) => { const a = ph + (i / n) * len * turns * Math.PI * 2, k = amp * Math.min(1, i / (n * 0.15)); return p.clone().addScaledVector(frames.normals[i], Math.cos(a) * k).addScaledVector(frames.binormals[i], Math.sin(a) * k); });
    }
    const curve = new THREE.CatmullRomCurve3(path), len = curve.getLength();
    const geo = new THREE.TubeGeometry(curve, Math.max(8, Math.round(len / (def.curl ? 0.0085 : 0.025))), def.thick * (0.85 + rnd() * 0.3), def.curl ? 4 : 5, false);
    // (the plait's bands along the strand at its real length: uv.x runs 0..1 along it)
    const uv = geo.getAttribute('uv');
    for (let i = 0; i < uv.count; i++) uv.setX(i, uv.getX(i) * len / 0.012);
    geos.push(geo);
  }
  if (!geos.length) return null;
  const merged = mergeGeometries(geos);
  for (const g of geos) g.dispose();
  if (!merged) return null;
  const mat = new THREE.MeshStandardMaterial({ color: colour, roughness: def.plait ? 0.75 : 0.5, metalness: 0, map: def.plait ? plaitTexture() : null });
  const mesh = new THREE.Mesh(merged, mat);
  mesh.castShadow = true;
  mesh.frustumCulled = false;
  return mesh;
}

/** Put this hairstyle on a skinned body (if it's one made in code); whatever code hair it had comes off. */
export function wearHairdo(root: THREE.Object3D, hair: string | null | undefined, colour: string): boolean {
  const old: THREE.Object3D[] = [];
  root.traverse((o) => { if (o.userData.hairdo) old.push(o); });
  for (const o of old) { o.removeFromParent(); const m = o as THREE.Mesh; m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
  const def = hairdoById(hair);
  if (!def) return false;
  const f = fitOf(root);
  if (!f) return false;
  const mesh = build(def, f, colour);
  if (!mesh) return false;
  const hi = Math.max(0, f.bones.findIndex((b) => b.name === 'head'));
  mesh.applyMatrix4(f.inv[hi]);
  mesh.userData.hairdo = def.id;
  f.bones[hi].add(mesh);
  return true;
}
